import React, { useState } from 'react';
import { TrainingData, Participant } from './TrainingWizard';
import { Button } from '../ui/Button';
import { PlusIcon, XIcon, CheckIcon, UserPlusIcon, SearchIcon, UserIcon, BuildingIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
// Mock data for clients
const mockClients = [{
  id: 1,
  name: 'Gemeente Rotterdam',
  type: 'government',
  contactPerson: 'Jan de Vries',
  email: 'j.devries@rotterdam.nl'
}, {
  id: 2,
  name: 'Transport BV',
  type: 'business',
  contactPerson: 'Marieke Jansen',
  email: 'm.jansen@transportbv.nl'
}, {
  id: 3,
  name: 'Zorg Nederland',
  type: 'healthcare',
  contactPerson: 'Peter Bakker',
  email: 'p.bakker@zorgnederland.nl'
}, {
  id: 4,
  name: 'Logistiek NL',
  type: 'business',
  contactPerson: 'Sophie Visser',
  email: 's.visser@logistieknl.nl'
}, {
  id: 5,
  name: 'Gemeente Utrecht',
  type: 'government',
  contactPerson: 'Hans Smit',
  email: 'h.smit@utrecht.nl'
}];
// Mock data for potential participants
const mockPotentialParticipants = [{
  id: 1,
  name: 'Jan Jansen',
  email: 'j.jansen@transportbv.nl',
  company: 'Transport BV'
}, {
  id: 2,
  name: 'Piet Peters',
  email: 'p.peters@logistieknl.nl',
  company: 'Logistiek NL'
}, {
  id: 3,
  name: 'Klaas Klaassen',
  email: 'k.klaassen@transportbv.nl',
  company: 'Transport BV'
}, {
  id: 4,
  name: 'Henk Hendriks',
  email: 'h.hendriks@rotterdam.nl',
  company: 'Gemeente Rotterdam'
}, {
  id: 5,
  name: 'Sjaak Swart',
  email: 's.swart@rotterdam.nl',
  company: 'Gemeente Rotterdam'
}, {
  id: 6,
  name: 'Dirk Kuijt',
  email: 'd.kuijt@transportbv.nl',
  company: 'Transport BV'
}, {
  id: 7,
  name: 'Anna Bakker',
  email: 'a.bakker@zorgnederland.nl',
  company: 'Zorg Nederland'
}, {
  id: 8,
  name: 'Marieke Visser',
  email: 'm.visser@zorgnederland.nl',
  company: 'Zorg Nederland'
}, {
  id: 9,
  name: 'Robert Janssen',
  email: 'r.janssen@utrecht.nl',
  company: 'Gemeente Utrecht'
}, {
  id: 10,
  name: 'Thomas Berg',
  email: 't.berg@utrecht.nl',
  company: 'Gemeente Utrecht'
}, {
  id: 11,
  name: 'Erik de Vries',
  email: 'e.devries@transportbv.nl',
  company: 'Transport BV'
}, {
  id: 12,
  name: 'Lisa Bakker',
  email: 'l.bakker@logistieknl.nl',
  company: 'Logistiek NL'
}, {
  id: 13,
  name: 'Peter Jansen',
  email: 'p.jansen@transportbv.nl',
  company: 'Transport BV'
}, {
  id: 14,
  name: 'Willem Willems',
  email: 'w.willems@transportbv.nl',
  company: 'Transport BV'
}, {
  id: 15,
  name: 'Sophie Smit',
  email: 's.smit@logistieknl.nl',
  company: 'Logistiek NL'
}];
interface ParticipantsStepProps {
  trainingData: TrainingData;
  updateTrainingData: (data: Partial<TrainingData>) => void;
}
export function ParticipantsStep({
  trainingData,
  updateTrainingData
}: ParticipantsStepProps) {
  const [selectedClient, setSelectedClient] = useState<number | null>(trainingData.clientId);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddParticipantForm, setShowAddParticipantForm] = useState(false);
  const [newParticipant, setNewParticipant] = useState({
    name: '',
    email: '',
    company: ''
  });
  // Handle client selection
  const handleClientSelect = (clientId: number) => {
    setSelectedClient(clientId);
    const client = mockClients.find(c => c.id === clientId);
    updateTrainingData({
      clientId,
      clientName: client?.name
    });
  };
  // Add a participant
  const addParticipant = (participant: Participant) => {
    const updatedParticipants = [...trainingData.participants, participant];
    updateTrainingData({
      participants: updatedParticipants
    });
  };
  // Remove a participant
  const removeParticipant = (participantId: number) => {
    const updatedParticipants = trainingData.participants.filter(p => p.id !== participantId);
    updateTrainingData({
      participants: updatedParticipants
    });
  };
  // Add a new manual participant
  const addManualParticipant = () => {
    if (newParticipant.name && newParticipant.email) {
      const newId = Math.max(0, ...mockPotentialParticipants.map(p => p.id), ...trainingData.participants.map(p => p.id)) + 1;
      const participant = {
        id: newId,
        name: newParticipant.name,
        email: newParticipant.email,
        company: newParticipant.company || (selectedClient ? mockClients.find(c => c.id === selectedClient)?.name || '' : '')
      };
      addParticipant(participant);
      setNewParticipant({
        name: '',
        email: '',
        company: ''
      });
      setShowAddParticipantForm(false);
    }
  };
  // Filter potential participants based on search and selected client
  const getFilteredParticipants = () => {
    return mockPotentialParticipants.filter(participant => {
      // Filter by search query
      const matchesSearch = participant.name.toLowerCase().includes(searchQuery.toLowerCase()) || participant.email.toLowerCase().includes(searchQuery.toLowerCase()) || participant.company.toLowerCase().includes(searchQuery.toLowerCase());
      // Filter by selected client
      const matchesClient = selectedClient ? participant.company === mockClients.find(c => c.id === selectedClient)?.name : true;
      // Exclude already added participants
      const notAlreadyAdded = !trainingData.participants.some(p => p.id === participant.id);
      return matchesSearch && matchesClient && notAlreadyAdded;
    });
  };
  const filteredParticipants = getFilteredParticipants();
  return <div data-id="element-3265">
      <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3266">
        Deelnemers Toevoegen
      </h2>
      <div className="space-y-6" data-id="element-3267">
        {/* Client selection */}
        <div data-id="element-3268">
          <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-3269">
            Selecteer Klant (Optioneel)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3270">
            {mockClients.map(client => <div key={client.id} className={`p-3 border rounded-lg cursor-pointer transition ${selectedClient === client.id ? 'border-2 border-[#B61C1C] bg-red-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} onClick={() => handleClientSelect(client.id)} data-id="element-3271">
                <div className="flex items-center" data-id="element-3272">
                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-3273">
                    <BuildingIcon className="h-4 w-4 text-gray-600" data-id="element-3274" />
                  </div>
                  <div className="ml-3" data-id="element-3275">
                    <h4 className="font-medium text-gray-900" data-id="element-3276">{client.name}</h4>
                    <p className="text-xs text-gray-600" data-id="element-3277">
                      {client.contactPerson}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
        {/* Participants list */}
        <div data-id="element-3278">
          <div className="flex justify-between items-center mb-2" data-id="element-3279">
            <h3 className="text-sm font-medium text-gray-700" data-id="element-3280">
              Geselecteerde Deelnemers ({trainingData.participants.length})
            </h3>
            <Button variant="outline" size="sm" onClick={() => setShowAddParticipantForm(!showAddParticipantForm)} data-id="element-3281">
              {showAddParticipantForm ? <>
                  <XIcon className="h-4 w-4 mr-1" data-id="element-3282" />
                  Annuleren
                </> : <>
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-3283" />
                  Handmatig Toevoegen
                </>}
            </Button>
          </div>
          {/* Add participant form */}
          {showAddParticipantForm && <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4" data-id="element-3284">
              <h4 className="font-medium text-gray-900 mb-3" data-id="element-3285">
                Nieuwe Deelnemer
              </h4>
              <div className="space-y-3" data-id="element-3286">
                <div data-id="element-3287">
                  <label className="block text-xs text-gray-500 mb-1" data-id="element-3288">
                    Naam
                  </label>
                  <input type="text" className="w-full p-2 border border-gray-300 rounded-md" value={newParticipant.name} onChange={e => setNewParticipant({
                ...newParticipant,
                name: e.target.value
              })} placeholder="Naam van deelnemer" data-id="element-3289" />
                </div>
                <div data-id="element-3290">
                  <label className="block text-xs text-gray-500 mb-1" data-id="element-3291">
                    Email
                  </label>
                  <input type="email" className="w-full p-2 border border-gray-300 rounded-md" value={newParticipant.email} onChange={e => setNewParticipant({
                ...newParticipant,
                email: e.target.value
              })} placeholder="Email van deelnemer" data-id="element-3292" />
                </div>
                <div data-id="element-3293">
                  <label className="block text-xs text-gray-500 mb-1" data-id="element-3294">
                    Bedrijf (Optioneel)
                  </label>
                  <input type="text" className="w-full p-2 border border-gray-300 rounded-md" value={newParticipant.company} onChange={e => setNewParticipant({
                ...newParticipant,
                company: e.target.value
              })} placeholder={selectedClient ? mockClients.find(c => c.id === selectedClient)?.name : 'Bedrijf van deelnemer'} data-id="element-3295" />
                </div>
                <div className="flex justify-end" data-id="element-3296">
                  <Button variant="primary" size="sm" onClick={addManualParticipant} disabled={!newParticipant.name || !newParticipant.email} data-id="element-3297">
                    <CheckIcon className="h-4 w-4 mr-1" data-id="element-3298" />
                    Toevoegen
                  </Button>
                </div>
              </div>
            </div>}
          {trainingData.participants.length > 0 ? <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3299">
              <ul className="divide-y divide-gray-200" data-id="element-3300">
                {trainingData.participants.map(participant => <li key={participant.id} className="p-3" data-id="element-3301">
                    <div className="flex justify-between items-center" data-id="element-3302">
                      <div className="flex items-center" data-id="element-3303">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-3304">
                          <UserIcon className="h-4 w-4 text-gray-600" data-id="element-3305" />
                        </div>
                        <div className="ml-3" data-id="element-3306">
                          <h4 className="font-medium text-gray-900" data-id="element-3307">
                            {participant.name}
                          </h4>
                          <p className="text-xs text-gray-600" data-id="element-3308">
                            {participant.email}
                          </p>
                          {participant.company && <p className="text-xs text-gray-500" data-id="element-3309">
                              {participant.company}
                            </p>}
                        </div>
                      </div>
                      <button className="text-red-500 hover:text-red-700" onClick={() => removeParticipant(participant.id)} data-id="element-3310">
                        <XIcon className="h-4 w-4" data-id="element-3311" />
                      </button>
                    </div>
                  </li>)}
              </ul>
            </div> : <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center" data-id="element-3312">
              <UserPlusIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" data-id="element-3313" />
              <p className="text-gray-500 mb-2" data-id="element-3314">
                Nog geen deelnemers toegevoegd
              </p>
              <p className="text-xs text-gray-400" data-id="element-3315">
                Voeg deelnemers toe via de zoekfunctie hieronder of handmatig
              </p>
            </div>}
        </div>
        {/* Search and add participants */}
        <div data-id="element-3316">
          <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-3317">
            Zoek Deelnemers
          </h3>
          <div className="relative mb-4" data-id="element-3318">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3319">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3320" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] sm:text-sm" placeholder="Zoeken op naam, email of bedrijf..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3321" />
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto" data-id="element-3322">
            {filteredParticipants.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-3323">
                {filteredParticipants.map(participant => <li key={participant.id} className="p-3 hover:bg-gray-50 cursor-pointer" onClick={() => addParticipant(participant)} data-id="element-3324">
                    <div className="flex justify-between items-center" data-id="element-3325">
                      <div className="flex items-center" data-id="element-3326">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-3327">
                          <UserIcon className="h-4 w-4 text-gray-600" data-id="element-3328" />
                        </div>
                        <div className="ml-3" data-id="element-3329">
                          <h4 className="font-medium text-gray-900" data-id="element-3330">
                            {participant.name}
                          </h4>
                          <p className="text-xs text-gray-600" data-id="element-3331">
                            {participant.email}
                          </p>
                          {participant.company && <Badge variant="default" className="mt-1 text-xs" data-id="element-3332">
                              {participant.company}
                            </Badge>}
                        </div>
                      </div>
                      <Button variant="outline" size="sm" data-id="element-3333">
                        <PlusIcon className="h-4 w-4" data-id="element-3334" />
                      </Button>
                    </div>
                  </li>)}
              </ul> : <div className="p-6 text-center" data-id="element-3335">
                <p className="text-gray-500" data-id="element-3336">
                  {searchQuery ? 'Geen deelnemers gevonden met deze zoekcriteria' : selectedClient ? 'Geen deelnemers beschikbaar voor deze klant' : 'Begin met zoeken om deelnemers toe te voegen'}
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}